import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';

import {
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Box,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';

export interface SegmentRow {
  field_name?: string;
  field_value?: string;
  qualifier?: string;
  primary_segment?: string;
  secondary_segment?: string;
  tertiary_segment?: string;
  priority?: string | number;
}

export type PrimarySegmentAvailabilityCounts = {
  sold: number;
  unsold: number;
};

interface GroupedSection {
  primarySegment: string;
  items: { row: SegmentRow; index: number }[];
}

interface SegmentSettingsTableProps {
  groupedSections: GroupedSection[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onEditPrimarySegment?: (primarySegment: string) => void;
  onDeletePrimarySegment?: (primarySegment: string) => void;
  loading?: boolean;
  actionsDisabled?: boolean;
  ariaLabelPrefix?: string;
  /**
   * Optional Sold/Unsold available counts by primary segment (lowercase keys).
   * When provided, chips render next to each primary segment header.
   */
  availabilityByPrimarySegment?: Record<string, PrimarySegmentAvailabilityCounts>;
  availabilityLoading?: boolean;
  availabilityRefreshLoading?: boolean;
  /** Same as Run Logs Preferences refresh — bypasses backend cache. */
  onRefreshAvailability?: () => void;
}

function lookupAvailability(
  map: Record<string, PrimarySegmentAvailabilityCounts> | undefined,
  primarySegment: string,
): PrimarySegmentAvailabilityCounts | undefined {
  if (!map) return undefined;
  const key = String(primarySegment ?? '').trim().toLowerCase();
  if (!key || key === '—') return undefined;
  return map[key];
}

export const SegmentSettingsTable = ({
  groupedSections,
  onEdit,
  onDelete,
  onEditPrimarySegment,
  onDeletePrimarySegment,
  loading = false,
  actionsDisabled = false,
  ariaLabelPrefix = 'segments',
  availabilityByPrimarySegment,
  availabilityLoading = false,
  availabilityRefreshLoading = false,
  onRefreshAvailability,
}: SegmentSettingsTableProps) => {
  const showAvailabilityChips = Boolean(availabilityByPrimarySegment) || availabilityLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {onRefreshAvailability && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: -0.5,
            mb: -0.5,
          }}
        >
          <Tooltip title="Refresh preferences">
            <span>
              <IconButton
                size="small"
                onClick={onRefreshAvailability}
                disabled={
                  availabilityRefreshLoading || availabilityLoading || loading
                }
                aria-label="Refresh preferences"
              >
                {availabilityRefreshLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      {groupedSections.map(({ primarySegment, items }) => {
        const availability = lookupAvailability(
          availabilityByPrimarySegment,
          primarySegment,
        );
        const soldCount = availability?.sold ?? 0;
        const unsoldCount = availability?.unsold ?? 0;

        return (
        <AccordionComponent
          key={primarySegment}
          elevation={0}
          sx={{
            '&::before': { display: 'none' },
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderRadius: '4px !important',
          }}
        >
          <AccordionSummaryComponent
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`primary-segment-${primarySegment}-content`}
            id={`primary-segment-${primarySegment}-header`}
            sx={{
              paddingLeft: 2,
              minHeight: 48,
              '&.Mui-expanded': {
                minHeight: 48,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  minWidth: 0,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Primary Segment - {primarySegment}
                </Typography>
                {showAvailabilityChips && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    onClick={event => event.stopPropagation()}
                    onFocus={event => event.stopPropagation()}
                  >
                    {availabilityLoading ? (
                      <>
                        <Skeleton
                          variant="rounded"
                          width={72}
                          height={24}
                          sx={{ borderRadius: 4 }}
                          aria-label={`${primarySegment} sold count loading`}
                        />
                        <Skeleton
                          variant="rounded"
                          width={88}
                          height={24}
                          sx={{ borderRadius: 4 }}
                          aria-label={`${primarySegment} unsold count loading`}
                        />
                      </>
                    ) : (
                      <>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Sold: ${soldCount}`}
                          aria-label={`${primarySegment} sold available ${soldCount}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Unsold: ${unsoldCount}`}
                          aria-label={`${primarySegment} unsold available ${unsoldCount}`}
                        />
                      </>
                    )}
                  </Box>
                )}
              </Box>
              {(onEditPrimarySegment || onDeletePrimarySegment) && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center' }}
                  onClick={event => event.stopPropagation()}
                  onFocus={event => event.stopPropagation()}
                >
                  {onEditPrimarySegment && (
                    <Tooltip title="Rename primary segment">
                      <span>
                        <IconButton
                          aria-label={`rename primary segment ${primarySegment}`}
                          size="small"
                          onClick={() => onEditPrimarySegment(primarySegment)}
                          disabled={loading || actionsDisabled}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {onDeletePrimarySegment && (
                    <Tooltip title="Delete entire primary segment">
                      <span>
                        <IconButton
                          aria-label={`delete primary segment ${primarySegment}`}
                          size="small"
                          onClick={() => onDeletePrimarySegment(primarySegment)}
                          disabled={loading || actionsDisabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
          </AccordionSummaryComponent>
          <AccordionDetailsComponent
            sx={{
              padding: 0,
              paddingTop: 0,
            }}
          >
            <Box sx={{ overflow: 'auto', width: '100%' }}>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label={`${ariaLabelPrefix} ${primarySegment}`}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Field Name</TableCell>
                    <TableCell>Field Value</TableCell>
                    <TableCell>Qualifier</TableCell>
                    <TableCell>Secondary Segment</TableCell>
                    <TableCell>Tertiary Segment</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(({ row, index }) => (
                    <TableRow key={index}>
                      <TableCell>{row.field_name ?? '—'}</TableCell>
                      <TableCell>{row.field_value ?? '—'}</TableCell>
                      <TableCell>{row.qualifier ?? '—'}</TableCell>
                      <TableCell>{row.secondary_segment ?? '—'}</TableCell>
                      <TableCell>{row.tertiary_segment ?? '—'}</TableCell>
                      <TableCell>{row.priority ?? '—'}</TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="edit"
                          onClick={() => onEdit(index)}
                          disabled={loading || actionsDisabled}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => onDelete(index)}
                          disabled={loading || actionsDisabled}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetailsComponent>
        </AccordionComponent>
        );
      })}
    </Box>
  );
};
